<template>
  <request-header-login-wrapper>
    <input-group :title="route.meta.title">
      <el-table class="text-xs" :data="messages" size="mini" border :max-height="`${tableHeight}px`">
        <el-table-column prop="sent_at" label="日時" width="160">
          <template #default="scope">
            <date-display :date="scope.row.sent_at" type="datetime" />
          </template>
        </el-table-column>
        <el-table-column prop="body" label="内容">
          <template #default="scope">
            <applicant-message-history-body
              v-if="Object.keys(scope.row).length > 0"
              :requestHeaderId="scope.row.request_header"
              :messageId="scope.row.message_id"
              :messageType="scope.row.message_type"
              :body="scope.row.body"
            />
          </template>
        </el-table-column>
        <el-table-column label="送信" show-overflow-tooltip>
          <template #default="scope">
            <message-sender-or-recipient-name
              :isApplicant="scope.row.is_applicant"
              :isApplicantHistory="true"
              :departmentMailName="scope.row.department?.department_mail_name"
            />
          </template>
        </el-table-column>
        <el-table-column label="" width="40">➡</el-table-column>
        <el-table-column label="受信">
          <template #default="scope">
            <el-tooltip
              :content="addressNameJoin(scope.row.message_address, '\n')"
              effect="dark"
              placement="top"
              popper-class="pre-wrap-tooltip"
            >
              <div
                class="recipient-row"
                v-html="`<span>${addressNameJoin(scope.row.message_address, '</span><span>')}</span>`"
              ></div>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </input-group>
  </request-header-login-wrapper>
</template>

<script lang="ts">
import { defineComponent, reactive, onBeforeMount, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AxiosResponse } from 'axios'
import { ApplicantHistoryListRequest } from '@/requests/message'
import { MessageApplicantHistory, MessageAddressDepartment, RequestHeaderDetail } from '@/api'
import RequestHeaderLoginWrapper from '@/components/auth-wrappers/RequestHeaderLoginWrapper.vue'
import InputGroup from '@/components/layout/InputGroup.vue'
import DateDisplay from '@/components/displays/DateDisplay.vue'
import ApplicantMessageHistoryBody from '@/components/displays/ApplicantMessageHistoryBody.vue'
import { displaySenderOrRecipientName } from '@/services/messageAddress'
import MessageSenderOrRecipientName from '@/components/displays/MessageSenderOrRecipientName.vue'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import Logger from '@/services/logger'
import { RequestTypes } from '@/constants/enums'

/**
 * 申請情報の取得
 */
const getRequestHeader = async (id: number): Promise<RequestHeaderDetail | null> => {
  const request = new RequestHeaderReadRequest(id)
  await request.exec()
  if (request.success) {
    const { data } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
    return data
  } else {
    return null
  }
}

/**
 * 申請者用回答履歴の表示コンポーネント
 * 受信欄はel-tooltip内でv-forを回せないため、繋げてから表示している
 */
export default defineComponent({
  name: 'ApplicantMessageHistory',
  components: {
    RequestHeaderLoginWrapper,
    InputGroup,
    DateDisplay,
    MessageSenderOrRecipientName,
    ApplicantMessageHistoryBody,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const messages = reactive<MessageApplicantHistory[]>([])
    const tableHeight = ref(0)
    const adjustHeight = 160
    const minHeight = 80
    // 申請情報
    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    const requestHeader = ref<RequestHeaderDetail | null>(null)

    // 受信者名の結合処理（重複は削除）
    const addressNameJoin = (addresses: MessageAddressDepartment[], delimiter: string): string => {
      const nameArray = addresses.map((address) =>
        displaySenderOrRecipientName({
          isApplicant: address.is_applicant,
          isApplicantHistory: true,
          departmentMailName: address.department?.department_mail_name,
        })
      )
      return [...new Set(nameArray)].join(delimiter)
    }

    onBeforeMount(async () => {
      // 申請基本を取得する
      if (requestHeaderId.value == null) {
        Logger.info(`URLの申請基本IDが不正です ${route.params.request_header_id}`)
        router.push({ name: 'NotFound' })
        return
      } else {
        requestHeader.value = await getRequestHeader(requestHeaderId.value)
        if (requestHeader.value?.request_type !== RequestTypes.PROXIMITY) {
          // 近接工事では無ければ404へ飛ばす
          router.push({ name: 'NotFound' })
          return
        }
      }
      // 回答履歴一覧を取得する
      const request = new ApplicantHistoryListRequest()
      await request.exec()
      if (request.success) {
        const { data } = request.getResponse() as AxiosResponse<MessageApplicantHistory[]>
        messages.push(...data)
      }
      // テーブルの高さ調整
      const calcHeight = window.innerHeight - adjustHeight
      tableHeight.value = calcHeight > minHeight ? calcHeight : minHeight
    })

    return {
      route,
      messages,
      tableHeight,
      addressNameJoin,
      displaySenderOrRecipientName,
    }
  },
})
</script>

<style lang="scss" scoped>
.input-group {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}
.text-omit {
  width: 100%;
  display: inline-block;
}
.recipient-row {
  line-height: initial;
}
::v-deep(.recipient-row > span) {
  width: 180px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
